/**
 * Shared API Service
 * 
 * Base API client configuration and utilities used across all applications.
 * 
 * Features:
 * - Axios instance with interceptors
 * - Automatic auth token handling
 * - Request/Response logging in development
 * - Error handling and transformation
 * - Retry logic for failed requests
 * 
 * @example
 * ```tsx
 * import { apiClient } from '@/shared/services';
 * 
 * const users = await apiClient.get('/users');
 * ```
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { env as runtimeEnv } from '../../config/runtimeEnv';

// Helper to safely access environment variables
const getEnv = () => {
  try {
    return process.env || {};
  } catch {
    return {};
  }
};

// =============================================================================
// TYPES
// =============================================================================

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  statusCode: number;
}

export interface ApiError {
  success: false;
  message: string;
  statusCode: number;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// =============================================================================
// API CLIENT FACTORY
// =============================================================================

/**
 * Create a configured Axios instance
 */
export function createApiClient(baseURL?: string): AxiosInstance {
  const env = getEnv();
  const isDev = process.env.NODE_ENV === 'development';

  // Diagnostic: Log environment variables
  if (isDev) {
    console.log('[API] Environment check:', {
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_API_URL: env.REACT_APP_API_URL,
      processEnv: Object.keys(process.env).filter(k => k.startsWith('REACT_APP_')),
    });
  }

  // Construct base URL from REACT_APP_API_URL
  const getBaseURL = () => {
    if (baseURL) {
      if (isDev) console.log('[API] Using provided baseURL:', baseURL);
      return baseURL;
    }
    const apiUrl = runtimeEnv.API_URL || runtimeEnv.REACT_APP_API_URL || runtimeEnv.VITE_API_BASE_URL || env.REACT_APP_API_URL;
    if (apiUrl) {
      // Ensure the URL ends with /api/v1
      const normalizedApiUrl = apiUrl.replace(/\/$/, '');
      const finalUrl = normalizedApiUrl.endsWith('/api/v1') ? normalizedApiUrl : `${normalizedApiUrl}/api/v1`;
      if (isDev) console.log('[API] Constructed baseURL from REACT_APP_API_URL:', { apiUrl, finalUrl });
      return finalUrl;
    }
    const fallbackUrl = '/api/v1';
    if (isDev) console.log('[API] Using fallback baseURL:', fallbackUrl);
    return fallbackUrl; // Fallback to relative path for localhost
  };

  const finalBaseURL = getBaseURL();
  if (isDev) console.log('[API] Creating axios client with baseURL:', finalBaseURL);
  const client = axios.create({
    baseURL: finalBaseURL,
    timeout: 30000,
    withCredentials: true, // Send cookies with requests
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor
  client.interceptors.request.use(
    (config) => {
      // Log requests in development
      if (isDev) {
        const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
        console.log(`🔵 [API] ${config.method?.toUpperCase()} ${fullUrl}`, {
          baseURL: config.baseURL,
          url: config.url,
          headers: config.headers,
        });
      }
      return config;
    },
    (error) => {
      if (isDev) console.error('❌ [API] Request error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor
  client.interceptors.response.use(
    (response) => {
      // Log responses in development
      if (isDev) {
        console.log(`🟢 [API] ${response.status} ${response.config.url}`);
      }
      return response;
    },
    (error: AxiosError<ApiError>) => {
      // Log errors
      if (isDev) {
        console.error(`🔴 [API] ${error.response?.status} ${error.config?.url}`, error.response?.data);
      }

      // Handle 401 - Unauthorized
      if (error.response?.status === 401) {
        // Check if we're not already on the login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }

      // Transform error for consistent handling
      const apiError: ApiError = {
        success: false,
        message: error.response?.data?.message || error.message || 'An unexpected error occurred',
        statusCode: error.response?.status || 500,
        errors: error.response?.data?.errors,
      };

      return Promise.reject(apiError);
    }
  );

  return client;
}

// =============================================================================
// DEFAULT CLIENT
// =============================================================================

export const apiClient = createApiClient();

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Helper to check if error is an API error
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'success' in error &&
    (error as ApiError).success === false
  );
}

/**
 * Helper to extract error message
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/**
 * Generic fetch with retry logic
 */
export async function fetchWithRetry<T>(
  request: () => Promise<T>,
  options: {
    retries?: number;
    delay?: number;
    onRetry?: (attempt: number, error: unknown) => void;
  } = {}
): Promise<T> {
  const { retries = 3, delay = 1000, onRetry } = options;
  let lastError: unknown;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await request();
    } catch (error) {
      lastError = error;

      // Don't retry on 4xx errors
      if (isApiError(error) && error.statusCode >= 400 && error.statusCode < 500) {
        throw error;
      }

      if (attempt < retries) {
        onRetry?.(attempt, error);
        await new Promise((resolve) => setTimeout(resolve, delay * attempt));
      }
    }
  }

  throw lastError;
}

export default apiClient;

